import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  MenuItem,
  Grid,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import config from '../../../config';

const SALARY_TYPES = ['Monthly', 'Daily', 'Hourly'];

// HR Employees master — list table + add/edit dialog (sectioned) +
// assign-shift dialog + read-only profile view. Code is auto-generated
// on the backend (EMP0001…) so we don't ask for it in the form.
export default function Employees() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const blankForm = {
    employeeName: '',
    mobile: '',
    email: '',
    address: '',
    departmentId: '',
    designationId: '',
    joiningDate: '',
    salaryType: 'Monthly',
    basicSalary: 0,
    bankName: '',
    bankAccountNo: '',
    bankIfsc: '',
    aadhaar: '',
    pan: '',
    biometricId: '',
    isActive: true,
  };
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  // Profile view
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [shiftHistory, setShiftHistory] = useState([]);

  // Assign-shift dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignShiftId, setAssignShiftId] = useState('');
  const [assignFromDate, setAssignFromDate] = useState(
    new Date().toISOString().split('T')[0],
  );

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ──────────────────────────────────────────────────────────────────
  // Initial reference data
  // ──────────────────────────────────────────────────────────────────
  const loadReferenceData = async () => {
    try {
      const [d, g, s] = await Promise.all([
        fetch(`${config.API_BASE_URL}/Hr/Departments/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${config.API_BASE_URL}/Hr/Designations/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${config.API_BASE_URL}/Hr/Shifts/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => (r.ok ? r.json() : [])),
      ]);
      setDepartments(d);
      setDesignations(g);
      setShifts(s);
    } catch (e) {
      /* non-blocking */
    }
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (departmentFilter) qs.append('departmentId', departmentFilter);
      if (designationFilter) qs.append('designationId', designationFilter);
      if (search) qs.append('search', search);
      qs.append('includeInactive', includeInactive);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Employees/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed');
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Server-side search hooks up to the same load() call. Debounced via a
  // short timeout so we don't refetch on every keystroke.
  useEffect(() => {
    if (!companyId) return;
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    departmentFilter,
    designationFilter,
    includeInactive,
    search,
  ]);

  // ──────────────────────────────────────────────────────────────────
  // Add / Edit
  // ──────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(blankForm);
    setActiveTab(0);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setActiveTab(0);
    // Pull the full profile so bank / ID / address fields populate.
    fetch(`${config.API_BASE_URL}/Hr/Employee/${row.id}/${companyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setForm({
          employeeName: d.employeeName || '',
          mobile: d.mobile || '',
          email: d.email || '',
          address: d.address || '',
          departmentId: d.departmentId || '',
          designationId: d.designationId || '',
          joiningDate: d.joiningDate
            ? d.joiningDate.split('T')[0]
            : '',
          salaryType: d.salaryType || 'Monthly',
          basicSalary: d.basicSalary || 0,
          bankName: d.bankName || '',
          bankAccountNo: d.bankAccountNo || '',
          bankIfsc: d.bankIfsc || '',
          aadhaar: d.aadhaar || '',
          pan: d.pan || '',
          biometricId: d.biometricId || '',
          isActive: !!d.isActive,
        });
        setDialogOpen(true);
      });
  };

  const save = async () => {
    if (!form.employeeName.trim()) {
      toast('Please enter employee name', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: editing?.id || null,
        CompanyId: companyId,
        EmployeeName: form.employeeName.trim(),
        Mobile: form.mobile || null,
        Email: form.email || null,
        Address: form.address || null,
        DepartmentId: form.departmentId || null,
        DesignationId: form.designationId || null,
        JoiningDate: form.joiningDate || null,
        SalaryType: form.salaryType,
        BasicSalary: Number(form.basicSalary) || 0,
        BankName: form.bankName || null,
        BankAccountNo: form.bankAccountNo || null,
        BankIfsc: form.bankIfsc || null,
        Aadhaar: form.aadhaar || null,
        Pan: form.pan || null,
        BiometricId: form.biometricId || null,
        IsActive: form.isActive,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Employee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.message || 'Save failed', 'error');
        return;
      }
      toast(editing ? 'Employee updated' : 'Employee added');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.employeeName}?`)) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Employee/${row.id}/${companyId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error();
      toast('Employee deleted');
      load();
    } catch (e) {
      toast('Delete failed', 'error');
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // Profile view (read-only)
  // ──────────────────────────────────────────────────────────────────
  const openProfile = async (row) => {
    try {
      const [pRes, hRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/Hr/Employee/${row.id}/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(
          `${config.API_BASE_URL}/Hr/EmployeeShiftHistory/${row.id}/${companyId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);
      if (pRes.ok) setProfileData(await pRes.json());
      if (hRes.ok) setShiftHistory(await hRes.json());
      setProfileOpen(true);
    } catch (e) {
      toast('Failed to load profile', 'error');
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // Assign shift
  // ──────────────────────────────────────────────────────────────────
  const openAssign = (row) => {
    setAssignTarget(row);
    setAssignShiftId(row.currentShiftId || '');
    setAssignFromDate(new Date().toISOString().split('T')[0]);
    setAssignOpen(true);
  };

  const saveAssign = async () => {
    if (!assignShiftId) {
      toast('Please pick a shift', 'warning');
      return;
    }
    try {
      const res = await fetch(`${config.API_BASE_URL}/Hr/AssignShift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CompanyId: companyId,
          EmployeeId: assignTarget.id,
          ShiftId: Number(assignShiftId),
          FromDate: assignFromDate,
          UserId: user?.id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Failed to assign shift', 'error');
        return;
      }
      toast('Shift assigned');
      setAssignOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        sx={{ mb: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#16a34a',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <PersonIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Employees
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR / Staff master · {rows.length} record(s)
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5} flexWrap='wrap'>
          <TextField
            select
            size='small'
            label='Department'
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 160 }}>
            <MenuItem value=''>All</MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.departmentName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size='small'
            label='Designation'
            value={designationFilter}
            onChange={(e) => setDesignationFilter(e.target.value)}
            sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 160 }}>
            <MenuItem value=''>All</MenuItem>
            {designations.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.designationName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size='small'
            placeholder='Search name / code / mobile'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 220 }}
          />
          <Stack direction='row' alignItems='center'>
            <Switch
              size='small'
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              Show inactive
            </Typography>
          </Stack>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
            Add Employee
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(15,23,42,0.06)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Shift</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Salary</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No employees found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.employeeCode}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.employeeName}
                      </TableCell>
                      <TableCell>{r.mobile || '—'}</TableCell>
                      <TableCell>{r.departmentName || '—'}</TableCell>
                      <TableCell>{r.designationName || '—'}</TableCell>
                      <TableCell>
                        {r.currentShiftName ? (
                          <Chip
                            size='small'
                            label={r.currentShiftName}
                            sx={{ bgcolor: '#e0f2fe', color: '#0369a1' }}
                          />
                        ) : (
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        ₹{Number(r.basicSalary || 0).toLocaleString('en-IN')}
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {r.salaryType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.isActive ? 'Active' : 'Inactive'}
                          sx={{
                            bgcolor: r.isActive ? '#dcfce7' : '#fee2e2',
                            color: r.isActive ? '#166534' : '#991b1b',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='View profile'>
                          <IconButton size='small' onClick={() => openProfile(r)}>
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Assign shift'>
                          <IconButton
                            size='small'
                            onClick={() => openAssign(r)}
                            sx={{ color: '#0ea5e9' }}>
                            <AccessTimeIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => openEdit(r)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ─── Add / Edit dialog ─── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing
            ? `Edit Employee — ${editing.employeeCode}`
            : 'New Employee'}
        </DialogTitle>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant='scrollable'
          sx={{ px: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Tab label='Basic' />
          <Tab label='Job & Salary' />
          <Tab label='Bank' />
          <Tab label='Identity' />
        </Tabs>
        <DialogContent dividers>
          {activeTab === 0 && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label='Employee Name *'
                  size='small'
                  fullWidth
                  value={form.employeeName}
                  onChange={(e) =>
                    setForm({ ...form, employeeName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Mobile'
                  size='small'
                  fullWidth
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Email'
                  size='small'
                  fullWidth
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Address'
                  size='small'
                  fullWidth
                  multiline
                  rows={2}
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                  <Typography sx={{ fontSize: '0.85rem' }}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label='Department'
                  size='small'
                  fullWidth
                  value={form.departmentId}
                  onChange={(e) =>
                    setForm({ ...form, departmentId: e.target.value })
                  }>
                  <MenuItem value=''>— None —</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.departmentName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label='Designation'
                  size='small'
                  fullWidth
                  value={form.designationId}
                  onChange={(e) =>
                    setForm({ ...form, designationId: e.target.value })
                  }>
                  <MenuItem value=''>— None —</MenuItem>
                  {designations
                    .filter(
                      (g) =>
                        !form.departmentId ||
                        g.departmentId === form.departmentId,
                    )
                    .map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.designationName}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type='date'
                  label='Joining Date'
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.joiningDate}
                  onChange={(e) =>
                    setForm({ ...form, joiningDate: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label='Salary Type'
                  size='small'
                  fullWidth
                  value={form.salaryType}
                  onChange={(e) =>
                    setForm({ ...form, salaryType: e.target.value })
                  }>
                  {SALARY_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type='number'
                  label='Basic Salary (₹)'
                  size='small'
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={form.basicSalary}
                  onChange={(e) =>
                    setForm({ ...form, basicSalary: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label='Bank Name'
                  size='small'
                  fullWidth
                  value={form.bankName}
                  onChange={(e) =>
                    setForm({ ...form, bankName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  label='Account Number'
                  size='small'
                  fullWidth
                  value={form.bankAccountNo}
                  onChange={(e) =>
                    setForm({ ...form, bankAccountNo: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label='IFSC'
                  size='small'
                  fullWidth
                  value={form.bankIfsc}
                  onChange={(e) =>
                    setForm({ ...form, bankIfsc: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 3 && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Aadhaar'
                  size='small'
                  fullWidth
                  value={form.aadhaar}
                  onChange={(e) =>
                    setForm({ ...form, aadhaar: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='PAN'
                  size='small'
                  fullWidth
                  value={form.pan}
                  onChange={(e) => setForm({ ...form, pan: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Biometric ID'
                  size='small'
                  fullWidth
                  value={form.biometricId}
                  helperText='For future biometric attendance device integration.'
                  onChange={(e) =>
                    setForm({ ...form, biometricId: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Assign shift dialog ─── */}
      <Dialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign Shift
          {assignTarget && (
            <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
              {assignTarget.employeeName} · {assignTarget.employeeCode}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              size='small'
              label='Shift *'
              value={assignShiftId}
              onChange={(e) => setAssignShiftId(e.target.value)}>
              {shifts.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.shiftName} ({s.startTime?.slice(0, 5)}–
                  {s.endTime?.slice(0, 5)})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type='date'
              size='small'
              label='From Date *'
              InputLabelProps={{ shrink: true }}
              value={assignFromDate}
              onChange={(e) => setAssignFromDate(e.target.value)}
            />
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              Any previous active shift will be auto-closed the day before this
              date.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveAssign}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Profile dialog (read-only) ─── */}
      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Employee Profile
        </DialogTitle>
        <DialogContent dividers>
          {!profileData ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {[
                ['Code', profileData.employeeCode],
                ['Name', profileData.employeeName],
                ['Mobile', profileData.mobile || '—'],
                ['Email', profileData.email || '—'],
                ['Department', profileData.departmentName || '—'],
                ['Designation', profileData.designationName || '—'],
                [
                  'Joining Date',
                  profileData.joiningDate
                    ? new Date(profileData.joiningDate).toLocaleDateString('en-IN')
                    : '—',
                ],
                [
                  'Current Shift',
                  profileData.currentShiftName || 'Unassigned',
                ],
                ['Salary Type', profileData.salaryType],
                [
                  'Basic Salary',
                  '₹' +
                    Number(profileData.basicSalary || 0).toLocaleString('en-IN'),
                ],
                ['Bank', profileData.bankName || '—'],
                ['Account', profileData.bankAccountNo || '—'],
                ['IFSC', profileData.bankIfsc || '—'],
                ['Aadhaar', profileData.aadhaar || '—'],
                ['PAN', profileData.pan || '—'],
                ['Biometric ID', profileData.biometricId || '—'],
                ['Address', profileData.address || '—'],
                [
                  'Status',
                  profileData.isActive ? 'Active' : 'Inactive',
                ],
              ].map(([k, v]) => (
                <Grid item xs={12} sm={6} key={k}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                    {k}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{v}</Typography>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Shift History
                </Typography>
                {shiftHistory.length === 0 ? (
                  <Typography sx={{ color: '#94a3b8' }}>
                    No shift assignments yet.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                          <TableCell>Shift</TableCell>
                          <TableCell>From</TableCell>
                          <TableCell>To</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shiftHistory.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.shiftName}</TableCell>
                            <TableCell>
                              {new Date(h.fromDate).toLocaleDateString('en-IN')}
                            </TableCell>
                            <TableCell>
                              {h.toDate
                                ? new Date(h.toDate).toLocaleDateString('en-IN')
                                : 'Current'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
